<template>
    <div id="account-selector">
      <div id="account-selector-dropdown">
        <v-autocomplete
          ref="autocomplete"
          v-model="selectedListId"
          outlined
          :label="label"
          item-text="name"
          item-value="id"
          item-color="primary"
          :clearable="clearable"
          :hide-details="hideDetails"
          :search-input.sync="searchList"
          :loading="listsLoading"
          :items="allLists"
          :return-object="returnObject"
          no-data-text="No list available"
          @change="emitOnChange"
          :rules="rules"
          :disabled="disabled"
        >
          <template #append-item>
            <v-responsive class="overflow-y-auto" max-height="400">
              <span v-intersect="loadNextPage" />
            </v-responsive>
          </template>
        </v-autocomplete>
  
        <slot name="append"></slot>
      </div>
    </div>
  </template>
  
  <script>
  import { isEmpty } from "@/utils";
  import { userLists } from "@/services/users";
  import { DEBOUNCE_MILLIS, DEFAULT_PER_PAGE_RECORDS } from "@/constants/app";
  import {mapActions} from "vuex";
  
  /**
   * AccountSelector
   * @shared component
   * @author {Jatin Kamboj}
   * @description Accounts selector
   */
  export default {
    name: "AccountSelector",
    /**
    |--------------------------------------------------
    | Custom events emitted by component
    |--------------------------------------------------
    */
    emits: ["changed"],
    /**
    |--------------------------------------------------
    | Props
    |--------------------------------------------------
    */
    props: {
      rules: { type: Array, default: () => [] },
      clearable: { type: Boolean, default: false },
      label: { type: String, default: "List Name" },
      returnObject: { type: Boolean, default: false },
      hideDetails: { type: [Boolean, String], default: false },
      disabled: { type: [Boolean, String], default: false },
    },
    /**
    |--------------------------------------------------
    | Data Properties
    |--------------------------------------------------
    */
    data() {
      return {
        loading: false,
        listsLoading: false,
        listMeta: {
          page: 1,
          per_page: DEFAULT_PER_PAGE_RECORDS,
          name: "",
        },
        userList: [],
        searchList: "",
        debounceTimer: null,
        selectedListId: "",
        selectedAccount:null
      };
    },
    /**
    |--------------------------------------------------
    | Watching properties
    |--------------------------------------------------
    */
    watch: {
        searchList(val) {
        if (!val) return;
  
        if (this.findList(val) == -1) {
          this.debounceAccountSearch();
        }
      },
    },
    /**
    |--------------------------------------------------
    | Computed Properties
    |--------------------------------------------------
    */
    computed: {
      allLists() {
        if (!isEmpty(this.userList)) return this.userList;
        else return [];
      },
      /**
       * Do user have more accounts ?
       */
      hasMoreAccounts() {
        if (!this.listMeta?.count) return true;
  
        return (
          this.listMeta?.page * this.listMeta?.per_page <
          this.listMeta?.count
        );
      },
    },
    /**
    |--------------------------------------------------
    | Methods
    |--------------------------------------------------
    */
    methods: {
      ...mapActions({
        setSnackbar: "ui/setSnackbar",
      }),
      /**
       * @emits changed
       */
      emitOnChange(value) {
        this.$emit("changed", value);
      },
      showApiError(data) {
        const message = data?.errors || data?.error;
        if (!message) return;

        this.setSnackbar({
          value: true,
          message,
          type: this.$appConfig.snackbar.snackbarTypes.error,
        });
      },
      buildListParams(params = {}) {
        const queryParams = {...params};
        const integrationType = this.$route.query.integration_type;

        if (integrationType) {
          queryParams.integration_type = integrationType;
        }

        return queryParams;
      },
      /**
       * Debounce search accounts
       */
      debounceAccountSearch() {
        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer);
        }
  
        this.debounceTimer = setTimeout(() => {
          const params = this.buildListParams({
            name: this.searchList,
            page: 1,
            per_page: DEFAULT_PER_PAGE_RECORDS,
          });
  
          if (this.defaultAccountDetails?.name !== this.searchList) {
            this.listMeta.name = this.searchList;
          }
          this.fetchLists(params);
          this.debounceTimer = null;
        }, DEBOUNCE_MILLIS);
      },
      /**
       * Merges accounts
       */
      mergeAccounts(data = {}) {
        return [...this.userList, ...(data?.data?.lists ?? [])];
      },
      /**
       * fetchLists
       * @description Fetches user accounts
       */
      async fetchLists(params = this.listMeta) {
        try {
          params = this.buildListParams(params);
          if (params.count) delete params.count;
  
          this.listsLoading = true;
          const {data} = await userLists(this.selectedAccount.id, params);

          if (data?.errors || data?.error) {
            this.showApiError(data);
            return;
          }
  
          if (!this.searchList && this.hasMoreAccounts) {
            this.listMeta = { ...data.data.meta };
          }
          this.userList = this.mergeAccounts(data);
        } catch (error) {
          this.showApiError(error.response?.data);
        } finally {
          this.listsLoading = false;
        }
      },
      findList(account) {
        const finder = ({ name }) => name.toLowerCase() == account.toLowerCase();
        return this.userList.findIndex(finder);
      },
      /**
       * Loads next page accounts records
       */
      loadNextPage() {
        if (this.listsLoading) return;
        if (this.hasMoreAccounts && this.listMeta?.count > 0) {
          this.listMeta.page++;
          this.fetchLists();
        }
      },
      reloadList(value){
        this.userList=[]
        this.selectedAccount = value
        if(this.selectedListId)
            this.selectedListId= null;
        if(value){
            this.fetchLists();
        }
      }
    },
  };
  </script>
  